import React, { Dispatch, SetStateAction, useRef } from 'react';
import { ReactComponent as Facebook } from '../icons/facebook.svg';
import { ReactComponent as Instagram } from '../icons/instagram.svg';
import { ReactComponent as LinkedIn } from '../icons/linkedIn.svg';
import { ReactComponent as Pinterest } from '../icons/pinterest.svg';
import { ReactComponent as Twitter } from '../icons/x.svg';
import { ReactComponent as Bitly } from '../icons/bitly.svg';
import { ReactComponent as Google } from '../icons/google.svg';
import { ReactComponent as Tiktok } from '../icons/tiktok.svg';
import { ReactComponent as Youtube } from '../icons/youtube.svg';
import { ReactComponent as Universal } from '../icons/universal.svg';
import { ReactComponent as Threads } from '../icons/threads.svg';
import classes from './ConnectProfile.module.scss';
import classNames from 'classnames';
import { capitalizeString } from '../../../helpers/capitalizeString';
import { Void } from '../../../models/basic-objects';
import { ListItemContainer } from '../../containers/ListItemContainer';
import { ContainerListItem } from '../../actions/ListItem/ListItem.view';
import { ListItem } from 'components/actions/ListItem';
import { OutsideClickWrapper } from '../../actions/OutsideClickWrapper';
import { Tooltip } from '../../actions/Tooltip';
import { profilesHelpers } from 'modules/Settings/Profiles/Profiles.helpers';
import { DropdownWrapper } from '../../containers/DropdownWrapper';
import { Network, SocialOAuthProvider } from '../../../models/graphql';

export type ConnectProfileViewProps = {
  action?: Void;
  listItems?: ContainerListItem[];
  expanded: boolean;
  oAuthProvider?: SocialOAuthProvider;
  network?: Network;
  setExpanded: Dispatch<SetStateAction<boolean>>;
};

export const ConnectProfileView = (props: ConnectProfileViewProps): JSX.Element => {
  let Icon: JSX.Element;
  let label = 'Connect';
  let tooltipText = '';

  const iconSize = { width: 24, height: 24 };

  if (
    props.oAuthProvider &&
    props.network !== Network.YOUTUBE &&
    props.network !== Network.GENERIC_PROFILE &&
    props.network !== Network.THREADS
  ) {
    tooltipText = `Make sure you are currently logged into the correct profile on  ${profilesHelpers.getDisplayProfileName(
      props.oAuthProvider
    )}`;
    switch (props.oAuthProvider) {
      case SocialOAuthProvider.FACEBOOK:
        Icon = <Facebook {...iconSize} />;
        tooltipText = 'Make sure you are currently logged into Facebook as an admin of the page';
        break;
      case SocialOAuthProvider.INSTAGRAM:
        Icon = <Instagram {...iconSize} />;
        break;
      case SocialOAuthProvider.LINKEDIN:
        Icon = <LinkedIn {...iconSize} />;
        break;
      case SocialOAuthProvider.PINTEREST:
        Icon = <Pinterest {...iconSize} />;
        break;
      case SocialOAuthProvider.TWITTER:
        Icon = <Twitter {...iconSize} />;
        break;
      case SocialOAuthProvider.BITLY:
        Icon = <Bitly {...iconSize} />;
        break;
      case SocialOAuthProvider.GOOGLE:
        Icon = <Google {...iconSize} />;
        break;
      case SocialOAuthProvider.TIKTOK:
        Icon = <Tiktok {...iconSize} />;
        break;
      default:
        Icon = <></>;
        break;
    }
  } else if (props.network === Network.GENERIC_PROFILE) {
    Icon = <Universal {...iconSize} />;
    label = 'Universal Profile';
    tooltipText = 'Coming Soon...';
  } else if (props.network === Network.THREADS) {
    Icon = <Threads {...iconSize} />;
    label = 'Coming Soon';
    tooltipText = 'Coming Soon...';
  } else if (props.network === Network.YOUTUBE) {
    Icon = <Youtube {...iconSize} />;
    label = 'Coming Soon';
    tooltipText = 'Coming Soon...';
  } else {
    Icon = <></>;
  }

  const portalWrapperRef = useRef<HTMLDivElement | null>(null);

  const clickOnOutsideHandler = () => {
    if (props.expanded) {
      props.setExpanded(false);
    }
  };

  const profileNetwork = capitalizeString(String(props.oAuthProvider ?? props.network).toLowerCase());
  const componentWrapperClasses = classNames({
    [classes.ConnectProfileWrapper]: true,
    [classes[`ConnectProfileWrapper${profileNetwork}`]]: true,
    [classes[`ConnectProfileWrapper${profileNetwork}Active`]]: props.expanded
  });

  return (
    <OutsideClickWrapper
      className={classes.ConnectRefWrapper}
      ref={portalWrapperRef}
      onClickOutside={clickOnOutsideHandler}
      onFocusOutside={clickOnOutsideHandler}>
      <a
        id={`connectProfile${props.network}`}
        className={componentWrapperClasses}
        onClick={e => {
          e.stopPropagation();
          props.action && props.action();
        }}>
        <figure>{Icon}</figure>
        <div className={classes.ConnectLabel}>{label}</div>
      </a>
      {props.listItems && props.listItems.length ? (
        <DropdownWrapper
          expanded={props.expanded}
          portalWrapperRef={portalWrapperRef}
          className={props.oAuthProvider === SocialOAuthProvider.INSTAGRAM ? classes.IGDropdownOverride : undefined}>
          <ListItemContainer type={'div'} expanded={props.expanded}>
            {props.listItems.map((item, index) => (
              <ListItem
                type={'div'}
                disabled={!!item.disabled}
                variation={'regular'}
                selected={false}
                key={index}
                action={() => {
                  item.action();
                  props.setExpanded(false);
                }}>
                {item.label}
              </ListItem>
            ))}
          </ListItemContainer>
        </DropdownWrapper>
      ) : null}
      <Tooltip pointer target={`#connectProfile${props.network}`} type={'regular'}>
        {tooltipText}
      </Tooltip>
    </OutsideClickWrapper>
  );
};
